import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ChevronRight, Zap, TrendingUp, Award, Clock } from 'lucide-react';

export const DAppCard = ({ dapp, featured = false }) => {
  const { t } = useLanguage();

  const getCategoryIcon = (category) => {
    const icons = {
      gaming: '🎮',
      social: '👥',
      defi: '💱',
      lending: '🏦',
      payments: '💳',
      nft: '🎨',
      gamification: '🏆',
      governance: '⚖️',
      security: '🛡️',
      rwa: '🏠',
    };
    return icons[category] || '📦';
  };

  const getPriorityClass = (priority) => {
    if (priority === 'Critical') return 'priority-critical';
    if (priority === 'High') return 'priority-high';
    return 'bg-slate-700/50 text-slate-300';
  };

  return (
    <div 
      className={`glass-card rounded-xl overflow-hidden dapp-card ${
        featured ? 'md:col-span-2 md:row-span-2' : ''
      }`}
      data-testid={`dapp-card-${dapp.id}`}
    >
      <div className="p-6 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{getCategoryIcon(dapp.category)}</span>
            <div>
              <h3 className={`font-semibold text-white ${featured ? 'text-xl md:text-2xl' : 'text-lg'}`}>
                {dapp.title}
              </h3>
              <span className="text-sm text-slate-400">{t(`categories.${dapp.category}`)}</span>
            </div>
          </div>
          {dapp.priority && (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityClass(dapp.priority)}`}>
              {dapp.priority}
            </span>
          )}
        </div>

        {/* Description */}
        <p className={`text-slate-300 mb-4 flex-grow ${featured ? 'text-base' : 'text-sm'}`}>
          {dapp.description}
        </p>

        {/* Metadata */}
        {dapp.impact && (
          <div className="space-y-3 mb-4">
            <div className="flex items-center gap-2 text-sm">
              <TrendingUp className="w-4 h-4 text-green-400" />
              <span className="text-slate-400">{t('top10.impact')}:</span>
              <span className="text-white">{dapp.impact}</span>
            </div>
            
            {dapp.incentive && (
              <div className="flex items-center gap-2 text-sm">
                <Award className="w-4 h-4 text-yellow-400" />
                <span className="text-slate-400">{t('top10.incentive')}:</span>
                <span className="text-white">{t(`incentives.${dapp.incentive}`)}</span>
              </div>
            )}
            
            {dapp.status && (
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-blue-400" />
                <span className="text-slate-400">{t('top10.status')}:</span>
                <Badge variant="outline" className="status-open text-xs">
                  {t(`statuses.${dapp.status}`)}
                </Badge>
              </div>
            )}
          </div>
        )}

        {/* CTA */}
        <Link to={`/apply?dapp=${encodeURIComponent(dapp.title)}`} data-testid={`apply-btn-${dapp.id}`}>
          <Button className="w-full mt-auto group glow-primary">
            {t('top10.applyBtn')}
            <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

// Compact card for Top 20
export const DAppCardCompact = ({ dapp }) => {
  const { t } = useLanguage();

  const getCategoryIcon = (category) => {
    const icons = {
      ai: '🤖', staking: '📈', payroll: '💰', crosschain: '🔗',
      data: '📊', insurance: '🛡️', nft: '🎨', creator: '✨',
      loyalty: '❤️', payments: '💳', oracle: '🔮', analytics: '📉',
      nocode: '🧩', launchpad: '🚀', bridge: '🌉', carbon: '🌱',
      supply: '📦', invoice: '📄', dao: '🏛️'
    };
    return icons[category] || '📦';
  };

  return (
    <div 
      className="glass-card rounded-lg p-4 dapp-card"
      data-testid={`dapp-card-compact-${dapp.id}`}
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl">{getCategoryIcon(dapp.category)}</span>
        <div className="flex-grow min-w-0">
          <h3 className="font-medium text-white text-sm truncate">{dapp.title}</h3>
          <p className="text-xs text-slate-400 mt-1 line-clamp-2">{dapp.description}</p>
        </div>
      </div>
      <Link to={`/apply?dapp=${encodeURIComponent(dapp.title)}`} className="mt-3 block" data-testid={`apply-compact-btn-${dapp.id}`}>
        <Button variant="outline" size="sm" className="w-full text-xs">
          {t('top20.applyBtn')}
        </Button>
      </Link>
    </div>
  );
};
